import { useEffect, useMemo, useState } from "react";
import { Box, Button, Chip, Paper, TextField, Typography } from "@mui/material";
import { useReportData } from "./UseReportData";
import { SectionFrame, fmt } from "./Reportkit";

const TYPES = ["FLHA", "NPT", "Breakdown", "Checklist"];
const BORDER = { FLHA: "#2A78D6", NPT: "#EDA100", Breakdown: "#D03B3B", Checklist: "#1BAF7A" };

function Highlight({ text, q }) {
  if (!q || !text) return text || null;
  const parts = text.split(new RegExp(`(${q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "ig"));
  return parts.map((p, i) => (p.toLowerCase() === q.toLowerCase() ? <mark key={i} style={{ background: "#F6EFD9", color: "inherit", borderRadius: 3, padding: "0 2px" }}>{p}</mark> : p));
}

export default function VoiceSection({ req }) {
  const [types, setTypes] = useState(TYPES);
  const [q, setQ] = useState("");
  const [dq, setDq] = useState("");
  const [limit, setLimit] = useState(50);

  useEffect(() => { const t = setTimeout(() => { setDq(q.trim()); setLimit(50); }, 300); return () => clearTimeout(t); }, [q]);

  const vreq = useMemo(() => ({ ...req, q: dq || null, types: types.length === TYPES.length ? null : types, limit, offset: 0 }), [req, dq, types, limit]);
  const st = useReportData("voice", vreq);
  const d = st.data;
  const toggle = (t) => { setLimit(50); setTypes((s) => (s.includes(t) ? (s.length > 1 ? s.filter((x) => x !== t) : s) : [...s, t])); };

  return (
    <SectionFrame eyebrow="Comments from the field" title="Operator Voice" desc="Every FLHA, NPT, breakdown and checklist comment — searchable and filterable" state={{ ...st, data: d || (st.loading ? null : {}) }}>
      <Paper variant="outlined" sx={{ p: 1.5, display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center" }}>
        <Typography variant="caption" fontWeight={700} color="text.secondary">TYPE</Typography>
        {TYPES.map((t) => <Chip key={t} size="small" label={t} onClick={() => toggle(t)} variant={types.includes(t) ? "filled" : "outlined"} color={types.includes(t) ? "primary" : "default"} />)}
        <TextField size="small" placeholder="Search comments, controls, categories…" value={q} onChange={(e) => setQ(e.target.value)} sx={{ minWidth: 300, flex: 1 }} />
        <Typography variant="caption" color="text.secondary">{d?.entries ? `Showing ${fmt(d.entries.length)} of ${fmt(d.total)}` : ""}</Typography>
      </Paper>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {(d?.entries || []).map((e, i) => (
          <Paper key={i} variant="outlined" sx={{ p: 1.5, borderLeft: 4, borderLeftColor: BORDER[e.type] || "divider" }}>
            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center", mb: 0.5, fontSize: 12, color: "text.secondary" }}>
              <Chip size="small" label={e.type} sx={{ height: 20, fontSize: 11 }} />
              <b style={{ color: "inherit" }}>{e.date} · {e.shift}</b>
              {e.crew && <span>Crew {e.crew}</span>}
              {e.unit && <span>{e.unit}</span>}
              <span>{e.category}</span>
              {e.detail && <span>{e.detail}</span>}
            </Box>
            <Typography variant="body2"><Highlight text={e.text} q={dq} /></Typography>
            {e.controls && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}><b>Controls: </b><Highlight text={e.controls} q={dq} /></Typography>}
          </Paper>
        ))}
        {d?.entries && d.entries.length === 0 && <Box sx={{ py: 5, textAlign: "center", color: "text.secondary" }}>No comments match these filters</Box>}
        {d?.entries && d.total > d.entries.length && <Button sx={{ alignSelf: "center" }} onClick={() => setLimit((l) => l + 100)}>Show more</Button>}
      </Box>
    </SectionFrame>
  );
}